import React, { useState } from 'react';
import { Typography, Box, Tabs, Tab, styled } from '@mui/material';
import {
  numberToCurrencyString,
  numberToPercentageString,
  CalendarDay,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Heading from 'components/Heading';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import { AllocationRule } from 'further-types/lp/allocations';
import GeneralCapitalCallAllocationsForm from './GeneralCapitalCallAllocationsForm';
import { useEqualisationSection } from '../EqualisationContext';

const TableContainer = styled('table')(({ theme }) => ({
  display: 'table',
  tableLayout: 'fixed',
  width: '100%',
  fontSize: theme.fontSizes?.sm,
  color: theme.palette.text.darkBodyText,
  textAlign: 'left',
  borderCollapse: 'collapse',
}));

const TableHeading = styled('th')(({ theme }) => ({
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  verticalAlign: 'top',
}));

const TableCell = styled('td')(({ theme }) => ({
  paddingRight: theme.spacing(2),
}));

interface GeneralCapitalCallCardProps {
  actionIndex: number;
  isLastElement: boolean;
  generalCapitalCall: {
    actionId: string;
    name: string;
    ledgerDate: CalendarDay;
    actionDueDate?: CalendarDay;
    totalAmountLcy: number;
    allocationRule: AllocationRule;
    originalInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
    }>;
    newInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
      excludeFromAllocation: boolean;
      callOverpaidOrUnderpaid: number;
    }>;
    numberOfEntries: number;
  };
  baseCurrency: CurrencyCode;
}

const GeneralCapitalCallCard: React.FC<GeneralCapitalCallCardProps> = ({
  isLastElement,
  generalCapitalCall,
  baseCurrency,
  actionIndex,
}) => {
  const isManualAllocationRule =
    generalCapitalCall.allocationRule === AllocationRule.Manual;
  const [activeTab, setActiveTab] = useState(1);
  const { closeDate } = useEqualisationSection();
  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
  };

  const concertinaItems = [
    <Typography key="name" variant="body2">
      {isManualAllocationRule
        ? `${generalCapitalCall.name}*`
        : generalCapitalCall.name}
    </Typography>,
    <Typography key="date" variant="body2">
      {generalCapitalCall.ledgerDate.toLocalDateLabel()}
    </Typography>,
    <Typography key="amount" variant="body2">
      {numberToCurrencyString(generalCapitalCall.totalAmountLcy, {
        currency: baseCurrency,
      })}
    </Typography>,
  ];

  const expandedContent = (
    <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
      >
        <Tab
          label={
            <Heading variant="h5" noMargin>
              Allocation before {closeDate?.toLocalDateLabel()}
            </Heading>
          }
        />
        <Tab
          label={
            <Heading variant="h5" noMargin>
              Allocation after {closeDate?.toLocalDateLabel()}
            </Heading>
          }
        />
      </Tabs>

      <Box mt={2} mb={2}>
        {activeTab === 0 && (
          <TableContainer>
            <thead>
              <tr>
                <TableHeading>Investor</TableHeading>
                <TableHeading>Total Commitment</TableHeading>
                <TableHeading>Called funds (LCY)</TableHeading>
                <TableHeading>Allocation % of called funds</TableHeading>
              </tr>
            </thead>
            <tbody>
              {generalCapitalCall.originalInvestorBreakdown.map((investor) => (
                <tr key={investor.commitmentId}>
                  <TableCell>{investor.investorName}</TableCell>
                  <TableCell>
                    {numberToCurrencyString(investor.totalCommitmentLcy, {
                      currency: baseCurrency,
                    })}
                  </TableCell>
                  <TableCell>
                    {numberToCurrencyString(investor.calledFundsLcy, {
                      currency: baseCurrency,
                    })}
                  </TableCell>
                  <TableCell>
                    {numberToPercentageString(investor.allocationPercentage, {
                      accuracy: 2,
                      noMultiply: true,
                    })}
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </TableContainer>
        )}
        {activeTab === 1 && (
          <GeneralCapitalCallAllocationsForm
            fields={generalCapitalCall.newInvestorBreakdown}
            totalAmountLcy={generalCapitalCall.totalAmountLcy}
            allocationRule={generalCapitalCall.allocationRule}
            actionIndex={actionIndex}
            baseCurrency={baseCurrency}
            originalInvestorBreakdown={
              generalCapitalCall.originalInvestorBreakdown
            }
          />
        )}
      </Box>
    </>
  );

  return (
    <EqualisationsConcertina
      isLastElement={isLastElement}
      expandedContent={expandedContent}
      concertinaItems={concertinaItems}
      gridTemplateColumns="60% 19% 19% 2%"
      isExpandedByDefault={isManualAllocationRule}
    />
  );
};

export default GeneralCapitalCallCard;
